import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { PendingUser } from "../../types/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Loader2, AlertCircle } from "lucide-react";
import { SuccessResponse } from "../../types/generated";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

export const PendingUsers = () => {
  const {
    data: usersResponse,
    isLoading,
    error,
  } = useQuery<SuccessResponse<PendingUser[]>, Error>({
    queryKey: ["admin-pending-users"],
    queryFn: () => apiService.getPendingUsers(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const users = usersResponse?.data || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Users</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span>Failed to load pending users</span>
          </div>
        ) : !users?.length ? (
          <div className="text-center text-muted-foreground py-8">
            No pending users at the moment
          </div>
        ) : (
          <>
            {/* Desktop View */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Registered</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
              {users.map((user) => (
                <Card key={user.id} className="p-4">
                  <div className="space-y-2">
                    <div>
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {user.email}
                      </div>
                    </div>
                    <div className="text-sm text-muted-foreground pt-2 border-t">
                      Registered:{" "}
                      {new Date(user.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
